import { useRef } from 'react'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { MessageType } from '../../../../utils/message-type'
import { MelddButton } from '../../../../@ui'
import {
  ConfirmDialogue,
  ConfirmDialogueRef,
} from '../../../../@ui/containers/modals/confirm.dialogue'
import {
  useUpgradeNodeTierMutation,
  UpgradeNodeTierInput,
} from '../../../../generated/graphql'

interface UpgradeNodeTierButtonProps {
  nodeTierId: string
  seats: number
  onUpgradeSuccess?: (
    billingId: string,
    amount: number,
    basePrice?: number
  ) => void
}

export const UpgradeNodeTierButton = ({
  nodeTierId,
  seats,
  onUpgradeSuccess,
}: UpgradeNodeTierButtonProps) => {
  const { t } = useTranslation('tiers')
  const confirmDialogRef = useRef<ConfirmDialogueRef>(null)
  const [upgradeNodeTier, { loading }] = useUpgradeNodeTierMutation()

  const handleUpgrade = async () => {
    try {
      const input: UpgradeNodeTierInput = {
        nodeTierId,
        seats,
      }

      const result = await upgradeNodeTier({
        variables: { input },
      })

      if (result.data?.upgradeNodeTier) {
        const { billingId, amount, basePrice } = result.data.upgradeNodeTier
        Toast.show(t('node-tier-upgrade-initiated'), MessageType.info)
        onUpgradeSuccess?.(
          billingId,
          +amount,
          basePrice ? +basePrice : undefined
        )
      }
    } catch (error) {
      console.error('Error upgrading node tier:', error)
      Sentry.captureException(error)
      Toast.show((error as Error).message, MessageType.error)
      return false
    }
  }

  return (
    <>
      <MelddButton
        onPress={() => confirmDialogRef.current?.open()}
        title={t('upgrade')}
        isDisabled={loading}
      />
      <ConfirmDialogue
        ref={confirmDialogRef}
        label={t('upgrade-node-tier-title')}
        onContinue={handleUpgrade}
        acceptLabel={t('upgrade')}
        cancelLabel={t('cancel')}
      >
        <Text>{t('upgrade-node-tier-message')}</Text>
      </ConfirmDialogue>
    </>
  )
}
